import React from 'react';
import { AppBar, Toolbar, SxProps, Theme } from '@mui/material';
import { useLiveTitlebarGeometry } from '../utils/wcoGeometry';

export const HEADER_HEIGHT = 32;

interface HubHeaderProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

const HubHeader: React.FC<HubHeaderProps> = ({ children, sx }) => {
  // When installed as a desktop PWA with the Window Controls Overlay active
  // (see manifest.json's display_override), this bar IS the title bar - the
  // OS draws its traffic-light/caption buttons directly on top of it instead
  // of in a separate native strip above. Hub is the top-level document, so
  // (unlike Suite's embedded header) it measures the real geometry itself via
  // JS instead of relying on plain CSS env(titlebar-area-*) - needed so the
  // macOS width correction in wcoGeometry.ts actually applies; falls back to
  // env() when WCO isn't active (regular browser tab, or an unsupported
  // platform).
  const geo = useLiveTitlebarGeometry();
  const toolbarSx = geo.active
    ? {
        minHeight: `${geo.height}px !important`,
        height: `${geo.height}px`,
        width: `${geo.width}px`,
        overflow: 'hidden' as const,
        pl: `${Math.max(12, geo.x || 0)}px`,
        pr: 1.5,
      }
    : {
        minHeight: `env(titlebar-area-height, ${HEADER_HEIGHT}px) !important`,
        height: `env(titlebar-area-height, ${HEADER_HEIGHT}px)`,
        width: 'env(titlebar-area-width, 100%)',
        // Without this, content that doesn't fit the narrowed width (e.g. on
        // Windows, where caption buttons eat into the right edge) visually
        // bled straight past the constraint into the reserved button area
        // instead of stopping at it.
        overflow: 'hidden' as const,
        pl: 'max(12px, env(titlebar-area-x, 0px))',
        pr: 1.5,
      };

  return (
    <AppBar position="static" elevation={0} sx={sx}>
      <Toolbar disableGutters sx={toolbarSx}>
        {children}
      </Toolbar>
    </AppBar>
  );
};

export default HubHeader;
